// v3.18-1336 APP-AUTO-001 — Application Ownership Resolution Modal.
//
// A BLOCKING modal (Radix "modal" prop, no outside dismiss) shown
// when an incoming or manually-created application cannot be
// auto-resolved.  The operator MUST resolve it before returning to
// the application.  The modal renders four action paths from the
// approved spec:
//   1. Create New Employee — inline slide-panel form.
//   2. Assign Existing Employee — top-scored candidates + free search.
//   3. Search Another Company — re-run resolution against a different
//      company.
//   4. Skip For Now — audit-only deferral.
//
// The modal never navigates away from the application.
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, UserPlus, UserCheck, Building2, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ROLES = [
  'sales_rep', 'vo_officer', 'concierge', 'funding',
  'applications', 'collections', 'manager', 'company_director',
];

export function ApplicationAssignmentModal({ assignmentId, isOpen, onResolved, onClose }) {
  const [loading, setLoading]     = useState(true);
  const [row, setRow]             = useState(null);
  const [tab, setTab]             = useState('assign');           // assign | create | company | skip
  const [pickedUserId, setPicked] = useState('');
  const [companyQuery, setCoQuery] = useState('');
  const [companyMatches, setCoMatches] = useState([]);
  const [busy, setBusy]           = useState(false);

  // Create-employee inline form.
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', role: 'sales_rep',
    operational_roles: [], vo_eligible: false,
  });

  // Load the assignment row and prepopulate the create form.
  useEffect(() => {
    if (!isOpen || !assignmentId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/application-assignments/${assignmentId}`);
        if (!alive) return;
        setRow(data.assignment);
        setForm(f => ({
          ...f,
          full_name: data.assignment.origin_rep_name || '',
          email:     data.assignment.origin_rep_email || '',
        }));
        // Pick a sensible default tab based on resolution status.
        if (data.assignment.resolution_status === 'multi_match') setTab('assign');
        else if (data.assignment.resolution_status === 'no_company') setTab('company');
        else setTab('create');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load assignment');
      } finally { setLoading(false); }
    })();
    return () => { alive = false; };
  }, [isOpen, assignmentId]);

  const submitSelect = async () => {
    if (!pickedUserId) { toast.error('Pick an employee to assign to.'); return; }
    setBusy(true);
    try {
      // v3.21-1815 — the backend now returns { resolved_client_id, target }
      // so the caller can jump straight to the exact Client with the
      // Ownership Information tab already open.
      const { data } = await api.post(`/application-assignments/${assignmentId}/select`, {
        user_id: pickedUserId, remember: true,
      });
      toast.success('Application assigned. Future applications will resolve automatically.');
      onResolved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to assign');
    } finally { setBusy(false); }
  };

  const submitCreateEmployee = async () => {
    if (!form.full_name || !form.email) {
      toast.error('Full Name and Email are required to create the employee.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/application-assignments/${assignmentId}/create-employee`,
        { ...form, company_id: row?.resolved_company_id, remember: true },
      );
      toast.success(`${data.user.full_name} created and assigned.`);
      // v3.21-1815 — surface the resolved client so the caller can
      // jump to Ownership Information immediately.
      onResolved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create employee');
    } finally { setBusy(false); }
  };

  const searchCompany = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/companies', {
        params: { q: companyQuery, limit: 20 },
      });
      setCoMatches(data?.companies || data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Company search failed');
    } finally { setBusy(false); }
  };

  const chooseCompany = async (companyId) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/application-assignments/${assignmentId}/search-company`,
        { company_id: companyId },
      );
      toast.success('Company re-resolved.');
      onResolved?.(data?.resolution);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Company re-resolve failed');
    } finally { setBusy(false); }
  };

  const submitSkip = async () => {
    setBusy(true);
    try {
      await api.post(`/application-assignments/${assignmentId}/skip`);
      toast.info('Deferred — this application stays in the queue.');
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to skip');
    } finally { setBusy(false); }
  };

  const missing_hint = useMemo(() => row?.hint || row?.origin_rep_name || '', [row]);

  return (
    <Dialog open={isOpen} onOpenChange={() => {}} modal>
      <DialogContent
        className="sm:max-w-[620px] p-0 rounded-2xl [&>button]:hidden max-h-[92vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="app-assign-modal"
      >
        <DialogTitle className="sr-only">Resolve Application Ownership</DialogTitle>
        <DialogDescription className="sr-only">
          Assign this application to an existing employee or create a new one.
        </DialogDescription>

        <div className="px-6 pt-5 pb-3 border-b border-slate-100">
          <div className="text-lg font-bold text-slate-900">Resolve Application Ownership</div>
          {row && (
            <div className="text-[13px] text-slate-600 mt-1 leading-snug"
                 data-testid="app-assign-hint">
              {row.hint || `Origin: ${row.origin_rep_name || '(no rep name)'} · ${row.origin_rep_email || '(no email)'} · ${row.resolved_company_name || row.origin_company_name}`}
            </div>
          )}
        </div>

        <div className="flex border-b border-slate-100 text-[12px] uppercase tracking-wide">
          {[
            ['assign',  'Assign Existing',  UserCheck],
            ['create',  'Create New',       UserPlus],
            ['company', 'Search Company',   Building2],
            ['skip',    'Skip For Now',     X],
          ].map(([k, label, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`flex-1 h-11 flex items-center justify-center gap-1
                ${tab === k ? 'text-slate-900 border-b-2 border-slate-900 bg-slate-50'
                             : 'text-slate-500 hover:text-slate-800'}`}
              data-testid={`app-assign-tab-${k}`}
            >
              <Icon size={12} />{label}
            </button>
          ))}
        </div>

        <div className="px-6 py-4">
          {loading && <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>}

          {!loading && tab === 'assign' && (
            <div className="space-y-2" data-testid="app-assign-existing-list">
              {(row?.candidates || []).length === 0 && (
                <div className="text-sm text-slate-500 italic">
                  No matching representatives at {row?.resolved_company_name}. Switch to &quot;Create New&quot; or &quot;Search Company&quot;.
                </div>
              )}
              {(row?.candidates || []).map(c => (
                <button
                  key={c.id}
                  onClick={() => setPicked(c.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded border ${pickedUserId === c.id ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                  data-testid={`app-assign-candidate-${c.id}`}
                >
                  <div className="text-left">
                    <div className="text-sm font-semibold">{c.full_name}</div>
                    <div className="text-[11px] text-slate-500">{c.email || '—'} · {c.role || '—'}</div>
                  </div>
                  <ChevronRight size={14} />
                </button>
              ))}
              <div className="pt-3">
                <Button
                  onClick={submitSelect}
                  disabled={busy || !pickedUserId}
                  className="w-full"
                  data-testid="app-assign-select-submit"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Assign to Selected
                </Button>
              </div>
            </div>
          )}

          {!loading && tab === 'create' && (
            <div className="space-y-2" data-testid="app-assign-create-form">
              <div className="text-[12px] text-slate-600">Prepopulated from the application. Complete any missing fields.</div>
              {/* v3.18-1341 — preserved incoming metadata surfaced so
                  the operator can see EVERY signal we captured before
                  they finish onboarding the new employee. */}
              {(row?.origin_rep_email || row?.origin_rep_name || row?.origin_rep_employee_id
                  || row?.lead_source || row?.submission_source || row?.integration_source
                  || row?.imported_timestamp) && (
                <div className="rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-slate-700"
                     data-testid="app-create-preserved-source">
                  <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold mb-1">
                    Preserved from incoming application
                  </div>
                  {row?.origin_rep_name && (
                    <div><span className="text-slate-500">Rep name:</span> {row.origin_rep_name}</div>
                  )}
                  {row?.origin_rep_email && (
                    <div><span className="text-slate-500">Rep email:</span> {row.origin_rep_email}</div>
                  )}
                  {row?.origin_rep_employee_id && (
                    <div><span className="text-slate-500">Employee ID:</span> {row.origin_rep_employee_id}</div>
                  )}
                  {row?.lead_source && (
                    <div><span className="text-slate-500">Lead source:</span> {row.lead_source}</div>
                  )}
                  {row?.submission_source && (
                    <div><span className="text-slate-500">Submission source:</span> {row.submission_source}</div>
                  )}
                  {row?.integration_source && (
                    <div><span className="text-slate-500">Integration:</span> {row.integration_source}</div>
                  )}
                  {row?.imported_timestamp && (
                    <div><span className="text-slate-500">Imported:</span> {row.imported_timestamp}</div>
                  )}
                </div>
              )}
              <Input value={form.full_name} onChange={(e) => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Full Name" data-testid="app-create-full-name" />
              <Input value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email (rep email preserved)" data-testid="app-create-email" />
              <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" data-testid="app-create-phone" />
              <select
                value={form.role}
                onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
                className="w-full h-9 text-sm border border-slate-200 rounded px-2 bg-white"
                data-testid="app-create-role"
              >
                {ROLES.map(r => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={form.vo_eligible}
                       onChange={(e) => setForm(f => ({ ...f, vo_eligible: e.target.checked }))}
                       data-testid="app-create-vo-eligible" />
                VO-eligible
              </label>
              <Button
                onClick={submitCreateEmployee}
                disabled={busy || !form.full_name || !form.email || !row?.resolved_company_id}
                className="w-full mt-2"
                data-testid="app-create-submit"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create Employee &amp; Assign
              </Button>
            </div>
          )}

          {!loading && tab === 'company' && (
            <div className="space-y-2" data-testid="app-assign-company-search">
              <div className="text-[12px] text-slate-600">Route this application to a different company.</div>
              <div className="flex gap-2">
                <Input value={companyQuery} onChange={(e) => setCoQuery(e.target.value)} placeholder="Company name…" data-testid="app-company-query" />
                <Button onClick={searchCompany} disabled={busy || !companyQuery} data-testid="app-company-search-btn">Search</Button>
              </div>
              <div className="space-y-1">
                {companyMatches.slice(0, 20).map(co => (
                  <button
                    key={co.id}
                    onClick={() => chooseCompany(co.id)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded border border-slate-200 hover:bg-slate-50"
                    data-testid={`app-company-choose-${co.id}`}
                  >
                    <div className="text-sm text-left">{co.name}</div>
                    <ChevronRight size={14} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {!loading && tab === 'skip' && (
            <div className="space-y-2 text-center py-4" data-testid="app-assign-skip">
              <div className="text-sm text-slate-700">
                Deferring keeps <b>{missing_hint || 'this application'}</b> in the resolution queue.
                The deferral is audited so a manager can pick it up later.
              </div>
              <Button onClick={submitSkip} disabled={busy} variant="outline"
                      data-testid="app-skip-submit" className="mx-auto">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Defer &amp; Close
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ApplicationAssignmentModal;
